import { Program } from "./programs.interface";
import { BackOfficeUser } from "./user.interface";
import { Organization } from "./organizations.interface";

export enum EnrollmentStatus {
  ACTIVE = "ACTIVE",
  COMPLETED = "COMPLETED",
  DISENROLLED = "DISENROLLED",
  PENDING_APPROVAL = "PENDING_APPROVAL",
  DENIED = "DENIED",
}

export enum RoleSubType {
  INTERNAL_PROVIDER = "INTERNAL_PROVIDER",
  SUPPORT_STAFF = "SUPPORT_STAFF",
}

export enum TransactionType {
  EARNED = "EARNED",
  REDEEMED = "REDEEMED",
  REFUNDED = "REFUNDED",
}

export enum MechanismType {
  TEST_RESULT = "TEST_RESULT",
  MOBILE_REDEEM = "MOBILE_REDEEM",
  INCENTIVE = "INCENTIVE",
}

export enum ChannelType {
  MOBILE = "MOBILE",
  BACKOFFICE = "BACKOFFICE",
}

export enum RedeemRequestStatus {
  PENDING_APPROVAL = "PENDING_APPROVAL",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
}

export enum RedeemableItemType {
  TIME_REDUCTION = "TIME_REDUCTION",
  MONEY_REDUCTION = "MONEY_REDUCTION",
  REMOTE_APPOINTMENT = "REMOTE_APPOINTMENT",
}

export enum EnrollmentAccessEnum {
  FULL_ACCESS = "FULL_ACCESS",
  READ_ONLY = "READ_ONLY",
  HEADER_ONLY = "HEADER_ONLY",
  NONE = "NONE",
}

export interface NotificationWindow {
  from?: string;
  to?: string;
}

export interface Invitations {
  invitationDate?: Date;
  acceptedDate?: Date;
  status?: string;
  code?: string;
}

export interface ConsentForm {
  s3Url?: string;
  dateOfProcess?: Date;
  status?: string;
  fileType?: string;
}

export interface Certificate {
  fullName?: string;
  dateOfCreation?: string;
  programName?: string;
  weeks?: string;
}

export interface Disenroll {
  reasons?: string;
  notes?: string;
  dateOfProcess?: string;
}

export interface Reenroll {
  previousEnrollmentId?: string;
  dateOfProcess?: string;
}
export interface VendorCard {
  vendor?: string;
  type?: string;
  orderId?: string;
  rewardId?: string;
  productId?: string;
  productName?: string;
  delivery?: string;
  link?: string;
  archive?: boolean;
}

export interface RewardEnrollmentBalance {
  enrollmentBalancesId: string;
  transactionIds: string[];
}

export interface Reward {
  _id?: string;
  participantId: string;
  enrollmentBalances: RewardEnrollmentBalance[];
  vendorCard?: VendorCard;
  date: string;
  amount: number;
  archive?: boolean;
}

export interface Transaction {
  _id: string;
  type: TransactionType;
  channel: ChannelType;
  description?: string;
  date: string;
  mechanism: string;
  operationId?: string;
  amount: number;
  researcher?: string;
  createdBy?: string;
  createdByName?: string;
  reward?: Reward;
}

export interface EnrollmentBalance {
  _id?: string;
  accountId: string;
  enrollmentId: string;
  transactions: Transaction[];
  total?: number;
}

export interface Enrollment {
  _id: string;
  accountId: string;
  participantId?: string;
  programId: string;
  orgUnitId: string;
  cmCoordinator: string;
  cmStartDate?: Date;
  cmEndDate?: Date;
  expectedEndDate?: Date;
  additionalStaff?: string[];
  enrollmentDate?: Date;
  status: EnrollmentStatus;
  notificationWindow?: NotificationWindow;
  invitations?: Invitations;
  consentForm?: ConsentForm;
  certificate?: Certificate;
  disenroll?: Disenroll;
  reenroll?: Reenroll;
  createdAt?: Date;
  updatedAt?: Date;
  cmCoordinatorUser?: BackOfficeUser;
  additionalStaffUser?: BackOfficeUser[];
  program?: Program;
  organization?: Organization;
  balance?: EnrollmentBalance;
  access?: EnrollmentAccessEnum;
  formFactor1?: string;
  formFactor2?: string;
  naloxoneId?: string;
}

export interface ProviderModuleItem {
  providerId: string;
  modulesIds: string[];
}

export interface ProviderModule extends ProviderModuleItem {
  id: string;
  name: string;
}

export interface UpdateModulesPayload {
  enrollmentId: string;
  providerId: string;
  modulesIds: string[];
  providerType: string;
}

export interface UpdateEnrollmentPayload {
  enrollmentId: string;
  enrollmentData: Partial<Enrollment>;
}

export interface UpdateProviderModulesPayload {
  enrollmentId: string;
  providerId: string;
  modulesIds: string[];
}

export interface RedeemableItem {
  _id: string;
  accountId: string;
  type: RedeemableItemType;
  description: string;
  points: number;
  redeemableByQuantity: boolean;
  preUnitText: string;
  unit: string;
  postUnitText: string;
}

export interface RedeemRequest {
  _id: string;
  accountId: string;
  redeemableItemId: string;
  quantity: number;
  processDate: Date;
  parcaPoints: number;
  status: "PENDING_APPROVAL" | "APPROVED" | "REJECTED";
  approvedBy: string;
  approvedDate?: Date;
  rejectedBy?: string;
  rejectedDate?: Date;
  rejectedComment?: string;
  redeemableItem: RedeemableItem;
  rejectedByOfficer?: string;
}

export interface EnrollmentBalanceSummary {
  enrollmentId: string;
  balancePoints: number;
  totalEarnedPoints: number;
  totalRedeemedPoints: number;
}

export interface Image {
  _id: string;
  base64image: string;
  name: string;
  description: string;
}

export type SearchRedeemRequestsPayload = {
  enrollmentId: string;
  sortField: string;
  sortOrder: number;
  pageNumber: number;
  pageSize: number;
  status: string[];
};

export interface SearchRedeemRequestsResponse {
  list: RedeemRequest[];
  totalCount: number;
}

export interface CasePlan {
  _id: string;
  fileName: string;
  url: string;
  dateCreated: Date;
}

export interface CreateEnrollment {
  participantId: string;
  createdBy: string;
  programId?: string | null;
  orgUnitId?: string | null;
  cmCoordinator?: string | null;
  additionalStaff?: string[] | null;
  notificationWindow?: NotificationWindow;
  formFactor1?: string | null;
  formFactor2?: string | null;
  naloxoneId?: string | null;
}

export interface DisenrollParams {
  participantId: string;
  enrollmentId: string;
  reason: string;
  updatedBy: string;
}

export interface RewardEngineResponse {
  type: string;
  params: RewardEngineResponseParams;
}

export interface RewardEngineResponseParams {
  totalIncentive: number;
  maxReached: boolean;
  recoveryAmount: number;
}

export interface EnrollmentTestResultsResponse {
  testResults: TestResult[];
  totalCount: number;
}

export interface TestResult {
  _id: string;
  status: string;
  validationType: string;
  rewardEngineResponse: RewardEngineResponse;
  createdAt: Date;
  completedAt?: string | Date; // ISO string from backend
  dayOfWeek: string;
  day: string;
  validationStatus: string;
  selfAssessResult: string;
  canValidate: boolean;
  rewardEarned: string;
  validationNumber: string;
  validationAttempts?: number | string; // COYN-1068: Real number of validation attempts (proofParticipant length)
}

export interface EnrollmentTestResultValidation {
  _id: string;
  isDeleted: boolean;
  createdBy: string;
  accountId: string;
  processDate: Date;
  status: string;
  validationNumber: number;
  validationType: string;
  enrollmentId: string;
  createdAt: Date;
  updatedAt: Date;
  __v: number;
  proofParticipant: ProofParticipant[];
}

export enum ProofAttemptStatusEnum {
  SUCCESS = "SUCCESS",
  FAILED = "FAILED",
}

export interface ProofParticipant {
  answer: boolean;
  attempt: number;
  s3Url: string;
  submissionDate: Date;
  type: string;
  status?: ProofAttemptStatusEnum;
}
